import {
  MATRIX_DESTINATAIRES,
  MATRIX_DESTINATAIRE_LABELS,
  MATRIX_DESTINATAIRE_NONE_LABEL,
  type MatrixDestinataire,
  type MatrixPivot,
  type MatrixSummary,
  matrixPivotSchema,
  resolveDestinataireValue,
} from '@mss_creator/shared';
import { z } from 'zod';

/**
 * The settings of a company's matrix grid (issue #16, `docs/specs/matrices.md`
 * « Barre de tri, filtres et vue »): view, one sort criterion and its
 * direction, and five cumulative filters.
 *
 * They live in the Matrices tab's URL search params, and the last ones are
 * also kept per company in `localStorage`, restored when the tab is reached
 * without params (breadcrumb, rail). Nothing is stored server-side. Both
 * sources are parsed leniently: an unknown value is dropped and its default
 * applies, it never fails the route.
 */

/** The four states a card shows. The order is the « État » sort order. */
export const MATRIX_DISPLAY_STATES = ['brouillon', 'echec', 'en_cours', 'terminee'] as const;
export type MatrixDisplayState = (typeof MATRIX_DISPLAY_STATES)[number];

export const MATRIX_DISPLAY_STATE_LABELS: Readonly<Record<MatrixDisplayState, string>> = {
  brouillon: 'Brouillon',
  echec: 'Échec',
  en_cours: 'Génération en cours',
  terminee: 'Terminée',
};

/** « Échec » is a draft whose last generation failed, not a stored state. */
export function matrixDisplayState(
  matrix: Pick<MatrixSummary, 'state' | 'lastGenerationFailedAt'>,
): MatrixDisplayState {
  if (matrix.state === 'généré') return 'terminee';
  if (matrix.state === 'génération_en_cours') return 'en_cours';
  return matrix.lastGenerationFailedAt !== null ? 'echec' : 'brouillon';
}

/**
 * What the « Destinataire » filter offers, in the wizard's order (issue #25):
 * « Non défini » first, spelled `non_defini` in the URL and the storage.
 * « Non défini » also holds the « Autre » left empty; « Autre » only the typed ones.
 */
export const DESTINATAIRE_FILTER_NONE = 'non_defini';
export const DESTINATAIRE_FILTER_VALUES = [
  DESTINATAIRE_FILTER_NONE,
  ...MATRIX_DESTINATAIRES,
] as const;
export type DestinataireFilterValue = (typeof DESTINATAIRE_FILTER_VALUES)[number];

export const DESTINATAIRE_FILTER_LABELS: Readonly<Record<DestinataireFilterValue, string>> = {
  [DESTINATAIRE_FILTER_NONE]: MATRIX_DESTINATAIRE_NONE_LABEL,
  ...MATRIX_DESTINATAIRE_LABELS,
};

/** The filter value a matrix falls under. */
export function destinataireFilterValue(
  matrix: Pick<MatrixSummary, 'destinataire' | 'destinataireAutre'>,
): DestinataireFilterValue {
  return resolveDestinataireValue(matrix) === null
    ? DESTINATAIRE_FILTER_NONE
    : (matrix.destinataire as MatrixDestinataire);
}

export const MATRIX_GRID_SORTS = ['created', 'updated', 'sujet', 'state', 'pivot'] as const;
export type MatrixGridSort = (typeof MATRIX_GRID_SORTS)[number];

export const MATRIX_GRID_SORT_LABELS: Readonly<Record<MatrixGridSort, string>> = {
  created: 'Date de création',
  updated: 'Date de modification',
  sujet: 'Sujet A-Z',
  state: 'État',
  pivot: 'Pivot',
};

export type SortDirection = 'asc' | 'desc';

/**
 * The direction a criterion starts in when picked: dates newest first, the
 * others in their natural order (A-Z, the state order, the pivot order).
 */
export const DEFAULT_SORT_DIRECTIONS: Readonly<Record<MatrixGridSort, SortDirection>> = {
  created: 'desc',
  updated: 'desc',
  sujet: 'asc',
  state: 'asc',
  pivot: 'asc',
};

export const MATRIX_GRID_VIEWS = ['grid', 'list'] as const;
export type MatrixGridView = (typeof MATRIX_GRID_VIEWS)[number];

export type CreatedByFilter = 'all' | 'me';

/** The « dossier » choice: every matrix, those not in a folder, or one folder's id. */
export const ALL_FOLDERS = 'toutes';
export const NO_FOLDER = 'hors';

/** What the tab shows from « Dossiers » (issue #19): navigation, not a filter. */
export type FolderSelection = string;

/** True for a folder id, i.e. neither of the two fixed views. */
export function isFolderId(selection: FolderSelection): boolean {
  return selection !== ALL_FOLDERS && selection !== NO_FOLDER;
}

/** Every setting resolved, defaults applied. What the tab renders from. */
export interface MatrixGridSettings {
  view: MatrixGridView;
  sort: MatrixGridSort;
  dir: SortDirection;
  q: string;
  states: MatrixDisplayState[];
  pivots: MatrixPivot[];
  destinataires: DestinataireFilterValue[];
  createdBy: CreatedByFilter;
  dossier: FolderSelection;
}

export const DEFAULT_MATRIX_GRID_SETTINGS: Readonly<MatrixGridSettings> = {
  view: 'grid',
  sort: 'created',
  dir: DEFAULT_SORT_DIRECTIONS.created,
  q: '',
  states: [],
  pivots: [],
  destinataires: [],
  createdBy: 'all',
  dossier: ALL_FOLDERS,
};

/**
 * The URL shape: only what differs from the defaults, so a pristine tab has a
 * clean URL and « no params » reliably means « restore the last settings ».
 */
export interface MatrixGridSearch {
  view?: MatrixGridView | undefined;
  sort?: MatrixGridSort | undefined;
  dir?: SortDirection | undefined;
  q?: string | undefined;
  states?: MatrixDisplayState[] | undefined;
  pivots?: MatrixPivot[] | undefined;
  destinataire?: DestinataireFilterValue[] | undefined;
  by?: CreatedByFilter | undefined;
  dossier?: FolderSelection | undefined;
}

/** A multi-value param: known values kept once each, in order; anything else dropped. */
function listOf<T extends string>(itemSchema: z.ZodType<T>) {
  return z
    .unknown()
    .optional()
    .transform((value): T[] => {
      const items: unknown[] = Array.isArray(value) ? value : value === undefined ? [] : [value];
      const kept: T[] = [];
      for (const item of items) {
        const parsed = itemSchema.safeParse(item);
        if (parsed.success && !kept.includes(parsed.data)) kept.push(parsed.data);
      }
      return kept;
    });
}

const matrixGridSearchInputSchema = z.object({
  view: z.enum(MATRIX_GRID_VIEWS).optional().catch(undefined),
  sort: z.enum(MATRIX_GRID_SORTS).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  // The router JSON-parses search values, so « ?q=2026 » arrives as a number.
  q: z.union([z.string(), z.number()]).transform(String).optional().catch(undefined),
  states: listOf(z.enum(MATRIX_DISPLAY_STATES)),
  pivots: listOf(matrixPivotSchema),
  // The former `pdv` (point de vue) param is not read: it is dropped without a message.
  destinataire: listOf(z.enum(DESTINATAIRE_FILTER_VALUES)),
  by: z.enum(['all', 'me']).optional().catch(undefined),
  // « toutes », « hors » or a folder id; anything else falls back to the default.
  dossier: z
    .union([z.enum([ALL_FOLDERS, NO_FOLDER]), z.string().uuid()])
    .optional()
    .catch(undefined),
});

/** Every setting, from whatever the URL or the storage held. */
export function resolveMatrixGridSettings(search: MatrixGridSearch): MatrixGridSettings {
  const sort = search.sort ?? DEFAULT_MATRIX_GRID_SETTINGS.sort;
  return {
    view: search.view ?? DEFAULT_MATRIX_GRID_SETTINGS.view,
    sort,
    dir: search.dir ?? DEFAULT_SORT_DIRECTIONS[sort],
    q: search.q ?? '',
    states: search.states ?? [],
    pivots: search.pivots ?? [],
    destinataires: search.destinataire ?? [],
    createdBy: search.by ?? 'all',
    dossier: search.dossier ?? ALL_FOLDERS,
  };
}

/** The minimal URL form of `settings`: defaults are left out. */
export function toMatrixGridSearch(settings: MatrixGridSettings): MatrixGridSearch {
  const search: MatrixGridSearch = {};
  if (settings.view !== DEFAULT_MATRIX_GRID_SETTINGS.view) search.view = settings.view;
  if (settings.sort !== DEFAULT_MATRIX_GRID_SETTINGS.sort) search.sort = settings.sort;
  if (settings.dir !== DEFAULT_SORT_DIRECTIONS[settings.sort]) search.dir = settings.dir;
  if (settings.q !== '') search.q = settings.q;
  if (settings.states.length > 0) search.states = settings.states;
  if (settings.pivots.length > 0) search.pivots = settings.pivots;
  if (settings.destinataires.length > 0) search.destinataire = settings.destinataires;
  if (settings.createdBy !== 'all') search.by = settings.createdBy;
  if (settings.dossier !== ALL_FOLDERS) search.dossier = settings.dossier;
  return search;
}

/**
 * Lenient, canonical parse of the raw search params (or of a stored value):
 * never throws, drops unknown keys and values, and leaves defaults out.
 */
export function parseMatrixGridSearch(raw: unknown): MatrixGridSearch {
  const input = typeof raw === 'object' && raw !== null ? raw : {};
  const parsed = matrixGridSearchInputSchema.parse(input);
  return toMatrixGridSearch(
    resolveMatrixGridSettings({
      view: parsed.view,
      sort: parsed.sort,
      dir: parsed.dir,
      q: parsed.q,
      states: parsed.states,
      pivots: parsed.pivots,
      destinataire: parsed.destinataire,
      by: parsed.by,
      dossier: parsed.dossier,
    }),
  );
}

export function isDefaultMatrixGridSearch(search: MatrixGridSearch): boolean {
  return Object.keys(parseMatrixGridSearch(search)).length === 0;
}

/** Exported for tests: the storage key of one company's grid settings. */
export function matrixGridStorageKey(companyId: string): string {
  return `companies.${companyId}.matrixGrid`;
}

/** The last settings stored for a company, or none when unset, unreadable or unavailable. */
export function readStoredMatrixGridSearch(companyId: string): MatrixGridSearch {
  let raw: string | null;
  try {
    raw = globalThis.localStorage?.getItem(matrixGridStorageKey(companyId)) ?? null;
  } catch {
    // Storage unavailable (private mode, blocked site data): nothing to restore.
    return {};
  }
  if (raw === null) return {};
  try {
    return parseMatrixGridSearch(JSON.parse(raw));
  } catch {
    // Not JSON: an unknown value, ignored like any other.
    return {};
  }
}

/** Remember a company's settings; the defaults clear the entry. */
export function storeMatrixGridSearch(companyId: string, search: MatrixGridSearch): void {
  const canonical = parseMatrixGridSearch(search);
  const key = matrixGridStorageKey(companyId);
  try {
    if (Object.keys(canonical).length === 0) {
      globalThis.localStorage?.removeItem(key);
    } else {
      globalThis.localStorage?.setItem(key, JSON.stringify(canonical));
    }
  } catch {
    // Storage unavailable: the settings still hold in the URL, they just
    // will not be restored from a bare link. Degrading beats failing the change.
  }
}
